import { useCallback } from 'react'
import { useStore } from 'jotai'
import * as Sentry from '@sentry/electron/renderer'
import { useUpdateStagedActionView } from '@renderer/overlay/Sidepanel/hooks/use-update-staged-action-view'
import {
  clearDraftContentIfUnchanged,
  clearDraftTitle,
  clearDraftTitleIfUnchanged,
  discardDraft,
  markDraftTitleError,
  resolveSavableTitle,
} from './inline-drafts'
import {
  actionInlineDraftsAtom,
  enqueueInlineDraftUpdate,
  titleCommitsInFlight,
  type TitleCommit,
} from './inline-draft-state'

interface ActionInlineDraftControllerOptions {
  actionId: string | undefined
  coreTitle: string | undefined
  serverContent: string | undefined
}

interface ActionInlineDraftController {
  commitTitle: () => Promise<boolean>
  discard: () => void
  isUpdating: boolean
  saveDraft: () => Promise<boolean>
}

/**
 * The single persistence boundary for an action's inline title and body edits.
 * Header blur, footer Save, and pre-invoke flushes may mount in different React
 * trees, but they all serialize through the per-action title queue here.
 */
export function useActionInlineDraftController({
  actionId,
  coreTitle,
  serverContent,
}: ActionInlineDraftControllerOptions): ActionInlineDraftController {
  const store = useStore()
  const { updateView, isUpdating } = useUpdateStagedActionView()

  const commitTitle = useCallback((): Promise<boolean> => {
    if (!actionId) {
      return Promise.resolve(true)
    }

    const inFlight = titleCommitsInFlight.get(actionId)
    if (inFlight) {
      inFlight.needsAnotherPass = true
      return inFlight.promise
    }

    const commit: TitleCommit = {
      needsAnotherPass: false,
      promise: Promise.resolve(true),
    }
    titleCommitsInFlight.set(actionId, commit)

    commit.promise = (async () => {
      do {
        commit.needsAnotherPass = false
        const title = resolveSavableTitle(store.get(actionInlineDraftsAtom)[actionId]?.title, coreTitle)
        if (!title) {
          store.set(actionInlineDraftsAtom, (drafts) => clearDraftTitle(drafts, actionId))
          continue
        }

        try {
          await enqueueInlineDraftUpdate(actionId, () => updateView(actionId, { title }))
        } catch (error) {
          Sentry.captureException(error, {
            tags: { area: 'action-inline-draft', operation: 'save-title' },
            extra: { actionId },
          })
          store.set(actionInlineDraftsAtom, (drafts) =>
            markDraftTitleError(drafts, actionId, 'Title could not be saved'),
          )
          return false
        }
        store.set(actionInlineDraftsAtom, (drafts) => clearDraftTitleIfUnchanged(drafts, actionId, title))
      } while (commit.needsAnotherPass)

      return true
    })().finally(() => {
      titleCommitsInFlight.delete(actionId)
    })

    return commit.promise
  }, [actionId, coreTitle, store, updateView])

  const saveDraft = useCallback(async (): Promise<boolean> => {
    if (!actionId) {
      return true
    }

    await Promise.resolve()
    if (!(await commitTitle())) {
      return false
    }

    const content = store.get(actionInlineDraftsAtom)[actionId]?.content
    if (content === undefined) {
      return true
    }
    if (content === serverContent) {
      store.set(actionInlineDraftsAtom, (drafts) => clearDraftContentIfUnchanged(drafts, actionId, content))
      return true
    }

    try {
      await enqueueInlineDraftUpdate(actionId, () => updateView(actionId, { content }))
    } catch (error) {
      Sentry.captureException(error, {
        tags: { area: 'action-inline-draft', operation: 'save-content' },
        extra: { actionId },
      })
      throw error
    }
    store.set(actionInlineDraftsAtom, (drafts) => clearDraftContentIfUnchanged(drafts, actionId, content))
    return true
  }, [actionId, commitTitle, serverContent, store, updateView])

  const discard = useCallback(() => {
    if (!actionId) {
      return
    }
    store.set(actionInlineDraftsAtom, (drafts) => discardDraft(drafts, actionId))
  }, [actionId, store])

  return { commitTitle, discard, isUpdating, saveDraft }
}
